<template>
  <div class="expense-create-update">
    <Loading v-if="loading" type="line" />

    <Alert v-if="isApproved" type="success">
      Despesa <b>Aprovada</b>, não pode ser alterada.
    </Alert>

    <b-row>
      <b-col xs="12" sm="12" md="12" lg="12" xl="12">
        <InputText
          ref="descriptionInput"
          title="Descrição"
          field="description"
          :formName="formName"
          :required="true"
          :maxLength="200"
          :disabled="isApproved"
          v-model="expense.description"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <InputText
          title="Documento"
          field="document"
          :formName="formName"
          :maxLength="100"
          :disabled="isApproved"
          v-model="expense.document"
        />
      </b-col>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <InputDecimal
          title="Valor"
          field="value"
          :required="true"
          :formName="formName"
          :disabled="isApproved"
          v-model="expense.value"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <DateTime
          title="Vencimento"
          field="dueDate"
          format="DD/MM/YYYY"
          :required="true"
          :formName="formName"
          :disabled="isApproved"
          v-model="expense.dueDate"
        />
      </b-col>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <DateTime
          title="Competência"
          field="competenceDate"
          format="DD/MM/YYYY"
          :formName="formName"
          :disabled="isApproved"
          v-model="expense.competenceDate"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <Select
          title="Tipo de Pagamento"
          field="typePayment"
          :formName="formName"
          :required="true"
          :showNewRegister="false"
          url="/api/v1/finance/type-payment/select-all"
          :disabled="isApproved"
          v-model="expense.typePayment"
        />
      </b-col>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <Select
          title="Plano de Contas"
          field="chartOfAccounts"
          :formName="formName"
          :showNewRegister="false"
          url="/api/v1/finance/chart-of-accounts/select-all"
          :disabled="isApproved"
          v-model="expense.chartOfAccounts"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <Select
          title="Destinatário (Nome ou CNPJ/CPF)"
          field="recipient"
          :required="true"
          :formName="formName"
          :showNewRegister="false"
          url="api/v1/finance/payable-receivable/select-all-recipient"
          :disabled="isApproved"
          v-model="expense.recipient"
        />
      </b-col>
      <b-col xs="12" sm="12" md="6" lg="6" xl="6">
        <Select
          title="Locação"
          field="rent"
          :formName="formName"
          :showNewRegister="false"
          url="/api/v1/rental/rent/select-all"
          :disabled="isApproved"
          v-model="expense.rent"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col xs="12" sm="12" md="12" lg="12" xl="12">
        <InputText
          title="Observação"
          field="observation"
          :formName="formName"
          :maxLength="500"
          :disabled="isApproved"
          v-model="expense.observation"
        />
      </b-col>
    </b-row>

    <div class="expense-create-update-footer">
      <CheckboxSimple
        v-if="!isApproved"
        title="Fechar ao salvar"
        field="closeOnSave"
        :formName="formName"
        :markFormDirty="false"
        v-model="closeOnSave"
      />
      <Button
        _key="btnSaveExpense"
        type="success"
        title="Salvar"
        classIcon="fas fa-save"
        size="medium"
        :disabled="isApproved"
        :loading="isLoading('btnSaveExpense')"
        :clicked="save"
      />
    </div>
  </div>
</template>

<script>
import InputText from "@/ui/forms/InputText";
import InputDecimal from "@/ui/forms/InputDecimal";
import DateTime from "@/ui/forms/DateTime";
import Select from "@/ui/forms/Select";
import Button from "@/ui/forms/Button";
import CheckboxSimple from "@/ui/forms/CheckboxSimple";
import Loading from "@/ui/shared/Loading.vue";
import Alert from "@/ui/layout/Alert.vue";

import Expense from "../models/Expense";
import ExpenseService from "../services/expense.service";

import { mapGetters, mapMutations } from "vuex";

export default {
  name: "ExpenseCreateUpdate",
  components: {
    InputText,
    InputDecimal,
    DateTime,
    Select,
    Button,
    CheckboxSimple,
    Loading,
    Alert,
  },
  props: {
    genericId: {
      type: String,
      default: null,
    },
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formName: "expenseCreateUpdate",
      loading: false,
      expense: new Expense(),
      closeOnSave: true,
    };
  },
  computed: {
    ...mapGetters("generic", ["isLoading"]),
    isApproved() {
      const status = (this.expense && (this.expense.statusName || this.expense.StatusName)) || "";
      return String(status).toLowerCase() === "aprovada";
    },
  },
  methods: {
    ...mapMutations("generic", ["removeLoading", "addNotification"]),
    ...mapMutations("validation", ["resetValidation"]),
    async save() {
      try {
        this.expense.genericId = this.genericId;
        if (this.expense.id) {
          await ExpenseService.update(this.expense);
        } else {
          await ExpenseService.create(this.expense);
        }
        this.$emit("saved", { closeOnSave: this.closeOnSave });
        if (!this.closeOnSave) {
          this.expense = new Expense();
          this.resetValidation(this.formName);
          this.$nextTick(() => {
            const input = this.$refs.descriptionInput && this.$refs.descriptionInput.$el.querySelector("input");
            if (input) input.focus();
          });
        }
      } finally {
        this.removeLoading(["btnSaveExpense"]);
      }
    },
  },
  watch: {
    value: {
      async handler(data) {
        if (data && data.id) {
          this.loading = true;
          try {
            const response = await ExpenseService.getById(data.id);
            this.expense.update(response.data.content);
          } finally {
            this.loading = false;
          }
        } else {
          this.expense = new Expense();
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped>
.expense-create-update {
  padding: 4px 0;
}

.expense-create-update-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 12px;
}

</style>
